/**
 * GPU compositor for the virtual background effect.
 *
 * Replaces the previous main-thread 2D-canvas + CSS `filter: blur()` compositing with a WebGL
 * pipeline:
 *   - the segmentation mask is uploaded as a small texture and upscaled with hardware bilinear
 *     filtering (plus a smoothstep) to feather the person edges,
 *   - the background blur is a separable Gaussian run at quarter resolution across a couple of
 *     ping-pong framebuffers (cheap on the GPU), and
 *   - the final masked blend happens in a single fragment shader.
 *
 * All of this runs on the GPU, freeing the main thread that the CSS-filter / getImageData /
 * putImageData path used to saturate. The renderer is intentionally self-contained and throws from
 * the constructor when WebGL/shaders are unavailable so the caller can fall back to the 2D path.
 */

const VERTEX_SHADER_SOURCE = `
attribute vec2 a_position;
attribute vec2 a_texCoord;
varying vec2 v_texCoord;
void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texCoord = a_texCoord;
}`;

// Standard 9-tap Gaussian, applied separably (horizontal then vertical).
const BLUR_FRAGMENT_SHADER_SOURCE = `
precision mediump float;
varying vec2 v_texCoord;
uniform sampler2D u_texture;
uniform vec2 u_step;
void main() {
    vec4 sum = texture2D(u_texture, v_texCoord) * 0.227027;
    sum += texture2D(u_texture, v_texCoord + u_step * 1.0) * 0.1945946;
    sum += texture2D(u_texture, v_texCoord - u_step * 1.0) * 0.1945946;
    sum += texture2D(u_texture, v_texCoord + u_step * 2.0) * 0.1216216;
    sum += texture2D(u_texture, v_texCoord - u_step * 2.0) * 0.1216216;
    sum += texture2D(u_texture, v_texCoord + u_step * 3.0) * 0.0540541;
    sum += texture2D(u_texture, v_texCoord - u_step * 3.0) * 0.0540541;
    sum += texture2D(u_texture, v_texCoord + u_step * 4.0) * 0.0162162;
    sum += texture2D(u_texture, v_texCoord - u_step * 4.0) * 0.0162162;
    gl_FragColor = sum;
}`;

// Foreground (sharp) over background (blurred frame or image), blended by the segmentation mask.
// All textures are sampled with the Y axis flipped so the output is upright on the canvas, and the
// background sources (frame texture / blur FBO / image) all share the same orientation.
const COMPOSITE_FRAGMENT_SHADER_SOURCE = `
precision mediump float;
varying vec2 v_texCoord;
uniform sampler2D u_frame;
uniform sampler2D u_background;
uniform sampler2D u_mask;
void main() {
    vec2 uv = vec2(v_texCoord.x, 1.0 - v_texCoord.y);
    float m = smoothstep(0.25, 0.75, texture2D(u_mask, uv).a);
    vec3 fg = texture2D(u_frame, uv).rgb;
    vec3 bg = texture2D(u_background, uv).rgb;
    gl_FragColor = vec4(mix(bg, fg, m), 1.0);
}`;

// Downsample factor for the blur framebuffers. Quarter resolution is plenty given the heavy blur.
const BLUR_DOWNSCALE = 4;

export default class WebGLBackgroundRenderer {
    /**
     * @param {number} width - Output width in pixels.
     * @param {number} height - Output height in pixels.
     */
    constructor(width, height, options = {}) {
        this.canvas = document.createElement('canvas');
        this.canvas.width = width;
        this.canvas.height = height;

        // Separable-blur iterations (each = 1 horizontal + 1 vertical pass). Fewer passes on low-end
        // GPUs: the background blur is slightly softer but cheaper.
        this._blurIterations = Math.max(1, options.blurIterations || 2);

        const contextAttributes = {
            alpha: false,
            depth: false,
            stencil: false,
            antialias: false,
            premultipliedAlpha: false,

            // Required so canvas.captureStream() reliably reads composited frames on all browsers.
            preserveDrawingBuffer: true
        };

        const gl = this.canvas.getContext('webgl', contextAttributes)
            || this.canvas.getContext('experimental-webgl', contextAttributes);

        if (!gl) {
            throw new Error('WebGL is not available');
        }

        this._gl = gl;
        this._contextLost = false;
        this._quarterW = Math.max(1, Math.floor(width / BLUR_DOWNSCALE));
        this._quarterH = Math.max(1, Math.floor(height / BLUR_DOWNSCALE));

        this._onContextLost = event => {
            event.preventDefault();
            this._contextLost = true;
        };
        this.canvas.addEventListener('webglcontextlost', this._onContextLost);

        this._blurProgram = this._buildProgram(VERTEX_SHADER_SOURCE, BLUR_FRAGMENT_SHADER_SOURCE, [ 'u_texture', 'u_step' ]);
        this._compositeProgram = this._buildProgram(
            VERTEX_SHADER_SOURCE,
            COMPOSITE_FRAGMENT_SHADER_SOURCE,
            [ 'u_frame', 'u_background', 'u_mask' ]
        );

        this._quadBuffer = this._createQuadBuffer();

        this._frameTexture = this._createTexture();
        this._maskTexture = this._createTexture();
        this._backgroundTexture = this._createTexture();

        // Initialise textures to 1x1 so they are complete before the first upload.
        const blackPixel = new Uint8Array([ 0, 0, 0, 255 ]);

        for (const texture of [ this._frameTexture, this._maskTexture, this._backgroundTexture ]) {
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, blackPixel);
        }

        this._fboA = this._createFramebuffer(this._quarterW, this._quarterH);
        this._fboB = this._createFramebuffer(this._quarterW, this._quarterH);
    }

    /**
     * Compiles a shader, throwing with the info log on failure.
     *
     * @param {number} type - gl.VERTEX_SHADER or gl.FRAGMENT_SHADER.
     * @param {string} source - GLSL source.
     * @returns {WebGLShader}
     */
    _compileShader(type, source) {
        const gl = this._gl;
        const shader = gl.createShader(type);

        gl.shaderSource(shader, source);
        gl.compileShader(shader);

        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            const log = gl.getShaderInfoLog(shader);

            gl.deleteShader(shader);
            throw new Error(`Shader compile failed: ${log}`);
        }

        return shader;
    }

    /**
     * Links a program and resolves the attribute / uniform locations used by the renderer.
     *
     * @param {string} vertexSource - Vertex shader source.
     * @param {string} fragmentSource - Fragment shader source.
     * @param {Array<string>} uniformNames - Uniforms to resolve.
     * @returns {Object} - { handle, aPosition, aTexCoord, <uniform>: location }.
     */
    _buildProgram(vertexSource, fragmentSource, uniformNames) {
        const gl = this._gl;
        const vertexShader = this._compileShader(gl.VERTEX_SHADER, vertexSource);
        const fragmentShader = this._compileShader(gl.FRAGMENT_SHADER, fragmentSource);
        const handle = gl.createProgram();

        gl.attachShader(handle, vertexShader);
        gl.attachShader(handle, fragmentShader);
        gl.linkProgram(handle);

        // The shaders are no longer needed once linked.
        gl.deleteShader(vertexShader);
        gl.deleteShader(fragmentShader);

        if (!gl.getProgramParameter(handle, gl.LINK_STATUS)) {
            const log = gl.getProgramInfoLog(handle);

            gl.deleteProgram(handle);
            throw new Error(`Program link failed: ${log}`);
        }

        const program = {
            handle,
            aPosition: gl.getAttribLocation(handle, 'a_position'),
            aTexCoord: gl.getAttribLocation(handle, 'a_texCoord')
        };

        for (const name of uniformNames) {
            program[name] = gl.getUniformLocation(handle, name);
        }

        return program;
    }

    /**
     * Builds the interleaved (position + texcoord) full-screen quad as a triangle strip.
     *
     * @returns {WebGLBuffer}
     */
    _createQuadBuffer() {
        const gl = this._gl;
        const buffer = gl.createBuffer();

        // x, y, u, v
        const data = new Float32Array([
            -1, -1, 0, 0,
            1, -1, 1, 0,
            -1, 1, 0, 1,
            1, 1, 1, 1
        ]);

        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);

        return buffer;
    }

    /**
     * Creates a CLAMP_TO_EDGE / LINEAR texture (valid for non-power-of-two sizes in WebGL1).
     *
     * @returns {WebGLTexture}
     */
    _createTexture() {
        const gl = this._gl;
        const texture = gl.createTexture();

        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        return texture;
    }

    /**
     * Creates a framebuffer backed by a color texture of the given size.
     *
     * @param {number} fbWidth - Width in pixels.
     * @param {number} fbHeight - Height in pixels.
     * @returns {Object} - { framebuffer, texture }.
     */
    _createFramebuffer(fbWidth, fbHeight) {
        const gl = this._gl;
        const texture = this._createTexture();

        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, fbWidth, fbHeight, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

        const framebuffer = gl.createFramebuffer();

        gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);

        return {
            framebuffer,
            texture
        };
    }

    /**
     * Binds the quad and points the given program's attributes at it, then draws.
     *
     * @param {Object} program - Program descriptor from {@code _buildProgram}.
     * @returns {void}
     */
    _drawQuad(program) {
        const gl = this._gl;

        gl.bindBuffer(gl.ARRAY_BUFFER, this._quadBuffer);
        gl.enableVertexAttribArray(program.aPosition);
        gl.vertexAttribPointer(program.aPosition, 2, gl.FLOAT, false, 16, 0);
        gl.enableVertexAttribArray(program.aTexCoord);
        gl.vertexAttribPointer(program.aTexCoord, 2, gl.FLOAT, false, 16, 8);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    /**
     * Uploads a new background image. Safe to call before/after rendering starts.
     *
     * @param {HTMLImageElement} image - A loaded image element.
     * @returns {void}
     */
    setBackgroundImage(image) {
        if (!this._gl || this._contextLost || !image || !image.complete || !image.naturalWidth) {
            return;
        }

        const gl = this._gl;

        try {
            gl.bindTexture(gl.TEXTURE_2D, this._backgroundTexture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        } catch (error) {
            console.warn('Failed to upload background image to GPU', error);
        }
    }

    /**
     * Uploads the latest segmentation mask. Called only when a new mask is computed (not every
     * composited frame) so uploads stay cheap.
     *
     * @param {ImageData} maskImageData - The mask, with the person probability in the alpha channel.
     * @returns {void}
     */
    updateMask(maskImageData) {
        if (!this._gl || this._contextLost) {
            return;
        }

        const gl = this._gl;

        gl.bindTexture(gl.TEXTURE_2D, this._maskTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, maskImageData);
    }

    /**
     * Runs the separable Gaussian blur over the current frame texture into {@code _fboB}.
     *
     * @param {number} blurValue - Requested blur strength (px); mapped to a texel radius.
     * @returns {void}
     */
    _renderBlur(blurValue) {
        const gl = this._gl;
        const qw = this._quarterW;
        const qh = this._quarterH;
        const radius = Math.min(Math.max(blurValue / 6, 1), 3);

        gl.useProgram(this._blurProgram.handle);
        gl.uniform1i(this._blurProgram.u_texture, 0);
        gl.activeTexture(gl.TEXTURE0);
        gl.viewport(0, 0, qw, qh);

        // First iteration, pass 1: frame -> A (horizontal, also downsamples to quarter res).
        gl.bindFramebuffer(gl.FRAMEBUFFER, this._fboA.framebuffer);
        gl.bindTexture(gl.TEXTURE_2D, this._frameTexture);
        gl.uniform2f(this._blurProgram.u_step, radius / qw, 0);
        this._drawQuad(this._blurProgram);

        // First iteration, pass 2: A -> B (vertical). Result now lives in _fboB.
        gl.bindFramebuffer(gl.FRAMEBUFFER, this._fboB.framebuffer);
        gl.bindTexture(gl.TEXTURE_2D, this._fboA.texture);
        gl.uniform2f(this._blurProgram.u_step, 0, radius / qh);
        this._drawQuad(this._blurProgram);

        // Additional iterations ping-pong B -> A -> B, so the result always ends up in _fboB.
        for (let i = 1; i < this._blurIterations; i++) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, this._fboA.framebuffer);
            gl.bindTexture(gl.TEXTURE_2D, this._fboB.texture);
            gl.uniform2f(this._blurProgram.u_step, radius / qw, 0);
            this._drawQuad(this._blurProgram);

            gl.bindFramebuffer(gl.FRAMEBUFFER, this._fboB.framebuffer);
            gl.bindTexture(gl.TEXTURE_2D, this._fboA.texture);
            gl.uniform2f(this._blurProgram.u_step, 0, radius / qh);
            this._drawQuad(this._blurProgram);
        }
    }

    /**
     * Uploads the current video frame and composites the final image to the canvas.
     *
     * @param {Object} params - Render parameters.
     * @param {HTMLVideoElement} params.video - Source video element (foreground).
     * @param {'blur'|'image'} params.mode - Background mode.
     * @param {number} [params.blurValue] - Blur strength when mode is 'blur'.
     * @returns {void}
     */
    render({ video, mode, blurValue = 8 }) {
        if (!this._gl || this._contextLost || !video || !video.videoWidth) {
            return;
        }

        const gl = this._gl;

        gl.bindTexture(gl.TEXTURE_2D, this._frameTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);

        let backgroundTexture;

        if (mode === 'image') {
            backgroundTexture = this._backgroundTexture;
        } else {
            this._renderBlur(blurValue);
            backgroundTexture = this._fboB.texture;
        }

        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        gl.useProgram(this._compositeProgram.handle);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this._frameTexture);
        gl.uniform1i(this._compositeProgram.u_frame, 0);

        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, backgroundTexture);
        gl.uniform1i(this._compositeProgram.u_background, 1);

        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, this._maskTexture);
        gl.uniform1i(this._compositeProgram.u_mask, 2);

        this._drawQuad(this._compositeProgram);
    }

    /**
     * Releases all GPU resources and the WebGL context.
     *
     * @returns {void}
     */
    dispose() {
        const gl = this._gl;

        if (!gl) {
            return;
        }

        this.canvas.removeEventListener('webglcontextlost', this._onContextLost);

        try {
            gl.deleteBuffer(this._quadBuffer);
            gl.deleteTexture(this._frameTexture);
            gl.deleteTexture(this._maskTexture);
            gl.deleteTexture(this._backgroundTexture);
            gl.deleteTexture(this._fboA.texture);
            gl.deleteTexture(this._fboB.texture);
            gl.deleteFramebuffer(this._fboA.framebuffer);
            gl.deleteFramebuffer(this._fboB.framebuffer);
            gl.deleteProgram(this._blurProgram.handle);
            gl.deleteProgram(this._compositeProgram.handle);

            const loseContext = gl.getExtension('WEBGL_lose_context');

            if (loseContext) {
                loseContext.loseContext();
            }
        } catch (error) {
            // Nothing actionable on teardown.
        }

        this._gl = null;
    }
}
